import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FileText, Upload, Check } from 'lucide-react';
import { API_URL } from '../../config';
import { showSuccess, showError } from '../../utils/toast';
import '../../App.css';

const usStates = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY',
];

const MedicalCardStep = ({ onNext, onBack, onSkip, onboardingData, setOnboardingData, userId }) => {
  const [cdlNumber, setCdlNumber] = useState(onboardingData.cdlNumber || '');
  const [cdlState, setCdlState] = useState(onboardingData.cdlState || '');
  const [dotNumber, setDotNumber] = useState(onboardingData.dotNumber || '');
  const [file, setFile] = useState(null);
  const [expirationDate, setExpirationDate] = useState('');
  const [examinerName, setExaminerName] = useState('');
  const [cardNumber, setCardNumber] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [uploaded, setUploaded] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [dotNumberError, setDotNumberError] = useState('');

  useEffect(() => {
    // Load user data if available
    const userData = localStorage.getItem('user');
    if (userData) {
      try {
        const user = JSON.parse(userData);
        setCdlNumber(user.cdlNumber || '');
        setCdlState(user.cdlState || '');
        setDotNumber(user.dotNumber || '');
      } catch (error) {
        console.error('Error parsing user data:', error);
      }
    }
  }, []);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (selectedFile.size > 10 * 1024 * 1024) {
        showError('File size must be less than 10MB');
        return;
      }
      setFile(selectedFile);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      if (droppedFile.size > 10 * 1024 * 1024) {
        showError('File size must be less than 10MB');
        return;
      }
      setFile(droppedFile);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleUpload = async () => {
    if (!file) {
      showError('Please select a file to upload');
      return;
    }

    setIsUploading(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);
      formData.append('documentType', 'medical_card');
      if (expirationDate) {
        formData.append('expirationDate', expirationDate);
      }
      if (examinerName) {
        formData.append('notes', `Examiner: ${examinerName}${cardNumber ? ` | Card #: ${cardNumber}` : ''}`);
      } else if (cardNumber) {
        formData.append('notes', `Card #: ${cardNumber}`);
      }

      await axios.post(`${API_URL}/api/documents`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });

      setUploaded(true);
      setOnboardingData({ ...onboardingData, medicalCardUploaded: true });
      showSuccess('Medical card uploaded successfully!');
    } catch (error) {
      console.error('Error uploading medical card:', error);
      showError('Failed to upload medical card. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  const handleContinue = async () => {
    setIsSaving(true);
    try {
      const token = localStorage.getItem('token');
      
      // Save CDL info to backend
      if (cdlNumber || cdlState || dotNumber) {
        setDotNumberError('');
        await axios.patch(
          `${API_URL}/api/users/${userId}/onboarding`,
          {
            cdlNumber: cdlNumber || undefined,
            cdlState: cdlState || undefined,
            dotNumber: dotNumber || undefined,
            progress: {
              step: 2,
              completed: ['welcome', uploaded ? 'medical' : null].filter(Boolean),
            },
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          }
        );
      }

      // Update onboarding data state
      const updatedData = {
        ...onboardingData,
        cdlNumber,
        cdlState,
        dotNumber,
        progress: {
          step: 2,
          completed: ['welcome', uploaded ? 'medical' : null].filter(Boolean),
        },
      };
      setOnboardingData(updatedData);
      onNext();
    } catch (error) {
      console.error('Error saving CDL info:', error);
      const payload = error?.response?.data;
      const apiMessage = typeof payload?.message === 'string'
        ? payload.message
        : Array.isArray(payload?.message) && payload.message.length > 0
          ? String(payload.message[0])
          : 'Failed to save information. Please try again.';

      if (error?.response?.status === 409) {
        setDotNumberError(apiMessage);
      } else {
        showError(apiMessage);
      }
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="onboarding-content">
      <h2 className="step-title">Medical Card & CDL Info</h2>
      <p className="step-description">
        Upload your medical card and add your CDL details
      </p>

      <div className="onboarding-form">
        {/* CDL Info Section - Optional */}
        <div className="cdl-info-section">
          <h3 className="onboarding-section-heading">
            CDL Information (Optional)
          </h3>

          <div className="form-group">
            <label>CDL Number</label>
            <input
              type="text"
              placeholder="A1234567"
              value={cdlNumber}
              onChange={(e) => setCdlNumber(e.target.value)}
            />
          </div>

          <div className="form-group">
            <label>CDL State</label>
            <select
              value={cdlState}
              onChange={(e) => setCdlState(e.target.value)}
            >
              <option value="">Select State</option>
              {usStates.map((state) => (
                <option key={state} value={state}>
                  {state}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>DOT Number (if you have operating authority)</label>
            <input
              type="text"
              placeholder="12345678"
              value={dotNumber}
              className={dotNumberError ? 'error' : ''}
              onChange={(e) => {
                setDotNumber(e.target.value);
                if (dotNumberError) setDotNumberError('');
              }}
            />
            {dotNumberError ? (
              <span className="error-text">{dotNumberError}</span>
            ) : null}
          </div>
        </div>

        <hr className="onboarding-divider" />

        {/* Medical Card Upload Section */}
        <div style={{ marginTop: '30px' }}>
          <h3 className="onboarding-section-heading">
            Medical Card
          </h3>
          <p className="onboarding-support-text">
            Upload your medical card to track expiration
          </p>
        <div className="file-upload-area" onDrop={handleDrop} onDragOver={handleDragOver}>
          {file ? (
            <div className="file-selected">
              <FileText size={24} className="file-icon" />
              <div>
                <p className="file-name">{file.name}</p>
                <p className="file-size">
                  {(file.size / 1024 / 1024).toFixed(2)} MB
                </p>
              </div>
              <button
                className="btn btn-sm btn-secondary"
                onClick={() => setFile(null)}
              >
                Remove
              </button>
            </div>
          ) : (
            <div className="file-upload-placeholder">
              <div className="upload-icon"><Upload size={32} /></div>
              <p>Drag & drop your medical card here</p>
              <p className="upload-hint">or click to browse</p>
              <input
                type="file"
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={handleFileChange}
                className="file-input"
              />
            </div>
          )}
        </div>

        {file && !uploaded && (
          <button
            className="btn btn-primary"
            onClick={handleUpload}
            disabled={isUploading}
            style={{ width: '100%', marginTop: '15px' }}
          >
            {isUploading ? 'Uploading...' : 'Upload Medical Card'}
          </button>
        )}

        {uploaded && (
          <div className="upload-success">
            <span className="success-icon"><Check size={24} /></span>
            <span>Medical card uploaded successfully!</span>
          </div>
        )}

        <div className="form-group" style={{ marginTop: '20px' }}>
          <label>Expiration Date (Optional)</label>
          <input
            type="date"
            value={expirationDate}
            onChange={(e) => setExpirationDate(e.target.value)}
          />
        </div>

        <div className="form-group">
          <label>Examiner Name (Optional)</label>
          <input
            type="text"
            value={examinerName}
            onChange={(e) => setExaminerName(e.target.value)}
            placeholder="Dr. John Smith"
          />
        </div>

        <div className="form-group">
          <label>Card Number (Optional)</label>
          <input
            type="text"
            value={cardNumber}
            onChange={(e) => setCardNumber(e.target.value)}
            placeholder="MC-123456"
          />
        </div>
        </div>
      </div>

      <div className="onboarding-buttons">
        <button className="btn btn-secondary skip-button" onClick={onSkip}>
          Skip for now
        </button>
        <button
          className="btn btn-primary"
          onClick={handleContinue}
          disabled={isSaving}
        >
          {isSaving ? 'Saving...' : 'Continue →'}
        </button>
      </div>
    </div>
  );
};

export default MedicalCardStep;

